import {
  CART_LOAD_STATUS,
  CART_SAVE_STATUS,
  groupCartLinesByDiameter,
  groupCartLinesByLensId,
  normalizeGridAxis,
  sortCartLinesByPower,
  SUBMIT_STATUS,
} from './cartUtils'
import { getDisplayLinePowers, toQuantityKey } from './gridAxisUtils'

const EMPTY_LINES = []
const EMPTY_QUANTITIES = {}
const EMPTY_RECAP = { groups: [], lineCount: 0, totalQuantity: 0 }

export const selectGridContext = state => state.bulkOrder.gridContext

export const selectCatalogUi = state => state.bulkOrder.catalogUi

export const selectCart = state => state.bulkOrder.cart

export const selectCartLines = state => {
  const lines = state.bulkOrder.cart?.lines || []

  return [...lines].sort((a, b) => {
    const nameCompare = a.lensName.localeCompare(b.lensName)
    if (nameCompare !== 0) return nameCompare
    return sortCartLinesByPower(a, b)
  })
}

export const getGridQuantities = ({
  selectedLensId,
  lines = EMPTY_LINES,
  diameter,
  side,
  cylinderSign,
  lensGridAxis,
} = {}) => {
  if (!selectedLensId || diameter == null) return EMPTY_QUANTITIES

  const quantities = (lines || EMPTY_LINES).reduce((nextQuantities, line) => {
    if (line.lensId !== selectedLensId) return nextQuantities
    if (line.diameter !== diameter) return nextQuantities
    if (line.side !== side) return nextQuantities

    const gridAxis = normalizeGridAxis(line.gridAxis ?? lensGridAxis)
    const display = getDisplayLinePowers(line, gridAxis, cylinderSign)
    const key = toQuantityKey(display.sphere, display.columnValue, gridAxis)
    nextQuantities[key] = line.quantity
    return nextQuantities
  }, {})

  return Object.keys(quantities).length === 0 ? EMPTY_QUANTITIES : quantities
}

export const selectGridQuantities = state => {
  const { selectedLensId, lensDetail, gridContext, cart } = state.bulkOrder
  const { diameter, side, cylinderSign } = gridContext || {}

  return getGridQuantities({
    selectedLensId,
    lines: cart?.lines,
    diameter,
    side,
    cylinderSign,
    lensGridAxis: lensDetail?.gridAxis,
  })
}

export const selectCartLineCount = state => (state.bulkOrder.cart?.lines || []).length

export const selectCartTotalQuantity = state =>
  (state.bulkOrder.cart?.lines || []).reduce((total, line) => total + line.quantity, 0)

export const selectCartLineGroups = state => groupCartLinesByLensId(selectCartLines(state))

export const getSelectedLensCartRecap = ({
  selectedLensId,
  lines = EMPTY_LINES,
} = {}) => {
  if (!selectedLensId) return EMPTY_RECAP

  const selectedLines = (lines || EMPTY_LINES).filter(
    line => line.lensId === selectedLensId,
  )
  if (selectedLines.length === 0) return EMPTY_RECAP

  return {
    groups: groupCartLinesByDiameter(selectedLines),
    lineCount: selectedLines.length,
    totalQuantity: selectedLines.reduce(
      (total, line) => total + line.quantity,
      0,
    ),
  }
}

export const selectSelectedLensCartRecap = state =>
  getSelectedLensCartRecap({
    selectedLensId: state.bulkOrder.selectedLensId,
    lines: state.bulkOrder.cart?.lines,
  })

export const selectCartReference = state => state.bulkOrder.cart?.reference || ''

export const selectCartRemark = state => state.bulkOrder.cart?.remark || ''

export const selectCartOwnerId = state => state.bulkOrder.cart?.cartOwnerId ?? null

export const selectCartLoadStatus = state =>
  state.bulkOrder.cart?.loadStatus || CART_LOAD_STATUS.IDLE

export const selectCartSaveStatus = state =>
  state.bulkOrder.cart?.saveStatus || CART_SAVE_STATUS.IDLE

export const selectIsCartLoading = state => selectCartLoadStatus(state) === CART_LOAD_STATUS.LOADING

export const selectHasCartError = state => selectCartLoadStatus(state) === CART_LOAD_STATUS.FAILED

export const selectHasCartSaveError = state => Boolean(state.bulkOrder.cart?.hasCartSaveError)

export const selectIsCartEditable = state => {
  const { cart, submitStatus } = state.bulkOrder
  return (
    cart?.loadStatus === CART_LOAD_STATUS.READY &&
    cart?.cartOwnerId != null &&
    submitStatus !== SUBMIT_STATUS.SUBMITTING
  )
}

export const selectIsCartSaving = state => selectCartSaveStatus(state) === CART_SAVE_STATUS.SAVING

export const selectIsCartEmpty = state => {
  const { cart } = state.bulkOrder
  const hasLines = (cart?.lines || []).length > 0
  const hasMeta = Boolean(cart?.reference?.trim()) || Boolean(cart?.remark?.trim())
  return !hasLines && !hasMeta
}

export const selectSubmitStatus = state =>
  state.bulkOrder.submitStatus || SUBMIT_STATUS.IDLE

export const selectIsSubmittingBulkOrder = state =>
  selectSubmitStatus(state) === SUBMIT_STATUS.SUBMITTING

export const selectBulkOrderHistory = state => state.bulkOrder.history

export const selectBulkOrders = state => state.bulkOrder.history?.orders || []

export const selectBulkOrdersMeta = state => state.bulkOrder.history?.meta || {}

export const selectBulkOrdersLoading = state => Boolean(state.bulkOrder.history?.loading)

export const selectBulkOrdersIsMasterUser = state =>
  Boolean(state.bulkOrder.history?.isMasterUser)

export const selectBulkOrdersSlaveUsers = state => state.bulkOrder.history?.slaveUsers || []

export const selectBulkOrderDetailState = state => state.bulkOrder.detail

export const selectBulkOrderDetail = state => state.bulkOrder.detail?.order || null

export const selectBulkOrderDetailLoading = state => Boolean(state.bulkOrder.detail?.loading)

export const selectBulkOrderDetailError = state => Boolean(state.bulkOrder.detail?.error)

export const selectBulkOrderDetailNotFound = state => Boolean(state.bulkOrder.detail?.notFound)

export const selectBulkOrderDetailLines = state => state.bulkOrder.detail?.order?.lines || []

export const selectBulkOrderDetailGroups = state =>
  groupCartLinesByLensId(selectBulkOrderDetailLines(state))

export const selectBulkOrderDetailLineCount = state => selectBulkOrderDetailLines(state).length

export const selectBulkOrderDetailTotalQuantity = state =>
  selectBulkOrderDetailLines(state).reduce((total, line) => total + (line.quantity || 0), 0)
